'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { cn, selectTriggerVariants, Spinner, Text, VirtualizedSingleSelect, type VirtualizedSingleSelectOption, type VirtualizedSingleSelectProps } from '@repo/ui';
import { Close } from '@repo/ui/icons';
import { abbreviateAddress, parsePtSymbol, type PtSymbol } from '@repo/utils';
import { useMemo, useState } from 'react';
import { type Address, getAddress, isAddress } from 'viem';
import type { ChainId, Token } from '../../web3.types';
import { getChainName } from '../../utils/chain.utils';
import { TokenIcon } from '../TokenIcon';
type TokenResolutionStatus = 'idle' | 'resolving' | 'invalid' | 'error';
type TokenListStatus = 'ready' | 'loading' | 'error';
type TokenSelectComboboxProps = Pick<VirtualizedSingleSelectProps, 'align' | 'side' | 'sideOffset' | 'variant' | 'size' | 'disabled' | 'placeholder' | 'triggerClassName' | 'data-testid'> & {
  tokens: Token[];
  chainId: ChainId;
  value: Token | null;
  onChange: (token: Token | null) => void;
  /** Mirrors the popover's search input so the consumer can resolve pasted addresses. */
  onSearchChange?: (search: string) => void;
  /**
   * A token the consumer resolved from the searched address, rendered as an
   * extra selectable row. A stale token (not matching the current search) is
   * filtered out, and one already in `tokens` is deduplicated — passing it is
   * always safe.
   */
  customToken?: Token | null;
  /** Drives the resolving/invalid empty states while the consumer resolves an address. */
  customTokenStatus?: TokenResolutionStatus;
  /** State of `tokens` itself, so an empty popover says "loading" or "unavailable" rather than "no tokens". */
  tokensStatus?: TokenListStatus;
};

/**
 * Token picker with a committed selection: while `value` is set the trigger
 * shows the token with a clear control and does not open; clearing returns to
 * the searchable popover. Arbitrary address entry is consumer-owned: mirror the
 * search via {@link TokenSelectComboboxProps.onSearchChange}, resolve it with
 * your own hook (e.g. an oRPC query), and pass the result back through
 * `customToken`/`customTokenStatus`. A resolved custom token is only shown
 * while the search matches it — it is never appended to the listed tokens.
 */
export function TokenSelectCombobox(props) {
  const $ = _c(49);
  let chainId;
  let customToken;
  let disabled;
  let onChange;
  let onSearchChange;
  let selectProps;
  let size;
  let t0;
  let t1;
  let t2;
  let testId;
  let tokens;
  let triggerClassName;
  let value;
  if ($[0] !== props) {
    ({
      tokens,
      chainId,
      value,
      onChange,
      onSearchChange,
      customToken,
      customTokenStatus: t0,
      tokensStatus: t1,
      variant: t2,
      size,
      disabled,
      triggerClassName,
      "data-testid": testId,
      ...selectProps
    } = props);
    $[0] = props;
    $[1] = chainId;
    $[2] = customToken;
    $[3] = disabled;
    $[4] = onChange;
    $[5] = onSearchChange;
    $[6] = selectProps;
    $[7] = size;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = testId;
    $[12] = tokens;
    $[13] = triggerClassName;
    $[14] = value;
  } else {
    chainId = $[1];
    customToken = $[2];
    disabled = $[3];
    onChange = $[4];
    onSearchChange = $[5];
    selectProps = $[6];
    size = $[7];
    t0 = $[8];
    t1 = $[9];
    t2 = $[10];
    testId = $[11];
    tokens = $[12];
    triggerClassName = $[13];
    value = $[14];
  }
  const customTokenStatus = t0 === undefined ? "idle" : t0;
  const tokensStatus = t1 === undefined ? "ready" : t1;
  const variant = t2 === undefined ? "secondary" : t2;
  const [search, setSearch] = useState("");
  let t3;
  if ($[15] !== customToken || $[16] !== tokens) {
    const byValue = new Map();
    const list = [];
    const addOption = token => {
      const optionValue = getAddress(token.address);
      if (byValue.has(optionValue)) {
        return;
      }
      byValue.set(optionValue, token);
      const pt = parsePtSymbol(token.symbol);
      list.push({
        value: optionValue,
        keywords: [token.name, token.symbol, ...(pt ? [pt.label, pt.maturityLabel] : [])],
        group: pt ? PT_GROUP : undefined,
        component: <TokenComboboxRow token={token} pt={pt} />
      });
    };
    const {
      base,
      pt: pt_0
    } = partitionPtTokens(customToken ? [...tokens, customToken] : tokens);
    base.forEach(addOption);
    pt_0.forEach(addOption);
    t3 = {
      options: list,
      tokenByValue: byValue
    };
    $[15] = customToken;
    $[16] = tokens;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const {
    options,
    tokenByValue
  } = t3;
  if (value) {
    let t4;
    if ($[18] !== onChange) {
      t4 = () => onChange(null);
      $[18] = onChange;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    let t5;
    if ($[20] !== disabled || $[21] !== size || $[22] !== t4 || $[23] !== testId || $[24] !== triggerClassName || $[25] !== value || $[26] !== variant) {
      t5 = <CommittedTokenTrigger token={value} onClear={t4} variant={variant} size={size} disabled={disabled} className={triggerClassName} data-testid={testId} />;
      $[20] = disabled;
      $[21] = size;
      $[22] = t4;
      $[23] = testId;
      $[24] = triggerClassName;
      $[25] = value;
      $[26] = variant;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    return t5;
  }
  let t4;
  if ($[28] !== onChange || $[29] !== tokenByValue) {
    t4 = optionValue_0 => {
      if (optionValue_0 == null) {
        return;
      }
      onChange(tokenByValue.get(optionValue_0) ?? null);
    };
    $[28] = onChange;
    $[29] = tokenByValue;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[31] !== onSearchChange) {
    t5 = nextSearch => {
      setSearch(nextSearch);
      onSearchChange?.(nextSearch);
    };
    $[31] = onSearchChange;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== chainId || $[34] !== customTokenStatus || $[35] !== search || $[36] !== tokensStatus) {
    t6 = getEmptyState({
      searchAddress: isAddress(search, {
        strict: false
      }) ? getAddress(search) : null,
      chainId,
      status: customTokenStatus,
      tokensStatus
    });
    $[33] = chainId;
    $[34] = customTokenStatus;
    $[35] = search;
    $[36] = tokensStatus;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== disabled || $[39] !== options || $[40] !== selectProps || $[41] !== size || $[42] !== t4 || $[43] !== t5 || $[44] !== t6 || $[45] !== testId || $[46] !== triggerClassName || $[47] !== variant) {
    t7 = <VirtualizedSingleSelect {...selectProps} variant={variant} size={size} disabled={disabled} triggerClassName={triggerClassName} data-testid={testId} clearable={false} options={options} value={null} onChange={t4} onSearchChange={t5} searchPlaceholder={"Search by name or address\u2026"} estimateItemSize={36} emptyState={t6} />;
    $[38] = disabled;
    $[39] = options;
    $[40] = selectProps;
    $[41] = size;
    $[42] = t4;
    $[43] = t5;
    $[44] = t6;
    $[45] = testId;
    $[46] = triggerClassName;
    $[47] = variant;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  return t7;
}
type CommittedTokenTriggerProps = Pick<TokenSelectComboboxProps, 'variant' | 'size' | 'disabled' | 'data-testid'> & {
  token: Token;
  onClear: () => void;
  className?: string;
};
function CommittedTokenTrigger(props) {
  const $ = _c(21);
  const {
    token,
    onClear,
    variant,
    size,
    disabled,
    className,
    "data-testid": testId
  } = props;
  const t0 = disabled ? "" : undefined;
  let t1;
  if ($[0] !== className || $[1] !== size || $[2] !== variant) {
    t1 = cn(selectTriggerVariants({
      variant,
      size
    }), "h-auto cursor-default hover:bg-secondary data-[disabled]:opacity-50", size === "sm" ? "min-h-7" : "min-h-8", className);
    $[0] = className;
    $[1] = size;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== token.symbol) {
    t2 = parsePtSymbol(token.symbol);
    $[4] = token.symbol;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2 || $[7] !== token) {
    t3 = <span className="flex min-w-0 items-center gap-1 break-words whitespace-normal"><TokenComboboxRow token={token} pt={t2} /></span>;
    $[6] = t2;
    $[7] = token;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = testId ? `${testId}-clear` : undefined;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Close className="size-3" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== disabled || $[11] !== onClear || $[12] !== t4) {
    t6 = <button type="button" aria-label="Clear token" disabled={disabled} onClick={onClear} className="text-muted-foreground hover:text-foreground ml-auto flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-sm disabled:cursor-not-allowed" data-testid={t4}>{t5}</button>;
    $[10] = disabled;
    $[11] = onClear;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== disabled || $[15] !== t0 || $[16] !== t1 || $[17] !== t3 || $[18] !== t6 || $[19] !== testId) {
    t7 = <div data-testid={testId} data-disabled={t0} aria-disabled={disabled} className={t1}>{t3}{t6}</div>;
    $[14] = disabled;
    $[15] = t0;
    $[16] = t1;
    $[17] = t3;
    $[18] = t6;
    $[19] = testId;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
const PT_GROUP = 'Pendle PT';

/** Non-PT tokens keep their incoming order; PT tokens follow, grouped and sorted by maturity. Partition the full candidate set so each group stays contiguous. */
function partitionPtTokens(tokens: Token[]): {
  base: Token[];
  pt: Token[];
} {
  const base: Token[] = [];
  const pt: {
    token: Token;
    maturity: Date;
  }[] = [];
  for (const token of tokens) {
    const parsed = parsePtSymbol(token.symbol);
    if (parsed) pt.push({
      token,
      maturity: parsed.maturity
    });else base.push(token);
  }
  pt.sort((a, b) => a.maturity.getTime() - b.maturity.getTime());
  return {
    base,
    pt: pt.map(entry => entry.token)
  };
}
type TokenComboboxRowProps = {
  token: Token;
  pt: PtSymbol | null;
};
function TokenComboboxRow(t0) {
  const $ = _c(17);
  const {
    token,
    pt
  } = t0;
  let t1;
  if ($[0] !== token.address || $[1] !== token.logoUrl || $[2] !== token.symbol) {
    t1 = <TokenIcon address={token.address} symbol={token.symbol} logoUrl={token.logoUrl} size="sm" />;
    $[0] = token.address;
    $[1] = token.logoUrl;
    $[2] = token.symbol;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = pt ? pt.label : token.symbol || token.name;
  let t3;
  if ($[4] !== t2) {
    t3 = <Text size="2xs" tag="span" className="text-foreground min-w-0 break-words whitespace-normal">{t2}</Text>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== pt) {
    t4 = pt && <Text size="4xs" tag="span" className="text-secondary-foreground shrink-0">{pt.maturityLabel}</Text>;
    $[6] = pt;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== token.address) {
    t5 = abbreviateAddress(token.address);
    $[8] = token.address;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <Text size="4xs" tag="span" className="text-secondary-foreground min-w-0 break-words whitespace-normal">{t5}</Text>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t4 || $[15] !== t6) {
    t7 = <span className="flex min-w-0 items-center gap-2">{t1}{t3}{t4}{t6}</span>;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
type GetEmptyStateParams = {
  searchAddress: Address | null;
  chainId: ChainId;
  status: TokenResolutionStatus;
  tokensStatus: TokenListStatus;
};
function getEmptyState(params: GetEmptyStateParams): ReactNode {
  const {
    searchAddress,
    chainId,
    status,
    tokensStatus
  } = params;
  if (searchAddress == null && tokensStatus === 'loading') {
    return <span className="flex items-center justify-center gap-2">
        <Spinner className="size-4" />
        <Text size="2xs" tag="span" className="text-secondary-foreground">
          Loading tokens…
        </Text>
      </span>;
  }
  if (searchAddress == null && tokensStatus === 'error') {
    return <span className="gap-2xs flex flex-col items-center">
        <Text size="2xs" tag="span" className="text-foreground">
          Token list unavailable
        </Text>
        <Text size="4xs" tag="span" className="text-secondary-foreground">
          Paste a token address to continue
        </Text>
      </span>;
  }
  if (searchAddress == null) {
    return <span className="gap-2xs flex flex-col items-center">
        <Text size="2xs" tag="span" className="text-foreground">
          No tokens found
        </Text>
        <Text size="4xs" tag="span" className="text-secondary-foreground">
          Try a different search
        </Text>
      </span>;
  }
  if (status === 'resolving') {
    return <span className="flex items-center justify-center gap-2">
        <Spinner className="size-4" />
        <Text size="2xs" tag="span" className="text-secondary-foreground">
          Resolving token…
        </Text>
      </span>;
  }
  if (status === 'invalid') {
    return <Text size="2xs" tag="span" className="text-secondary-foreground">
        This address is not a valid ERC-20 token on {getChainName(chainId)}
      </Text>;
  }
  if (status === 'error') {
    return <Text size="2xs" tag="span" className="text-secondary-foreground">
        Could not verify this address. Please try again.
      </Text>;
  }
  return <Text size="2xs" tag="span" className="text-foreground">
      No tokens found
    </Text>;
}
export type { TokenListStatus, TokenResolutionStatus, TokenSelectComboboxProps };